import Autocomplete from '@mui/material/Autocomplete'
import CaleoInputLabel from 'Components/reusable/CaleoCustomComponents/CaleoInputLabel'
import React from 'react'
import { IAllocation } from 'types/assignmentInterfaces'
import { StateCode } from 'types/ids'
import { fuseFiltering } from 'utils/utils'
import { InputField } from '../InputField'

/** @notExported */
export interface IStateOption {
  state: StateCode | number | string
  label: string
}

/** @notExported */
interface IStatePickerProps {
  /** The id. */
  id?: string
  /** The current value. */
  value: StateCode | null
  /** Action to do when the value changes. */
  onChange: (newValue: IAllocation | null) => void
  /** The label text. */
  label?: string
  /** Whether the field is required. */
  required?: boolean
  /** Whether the field is disabled. */
  disabled: boolean
  /** The available options. */
  stateOptions: IStateOption[]
  /** Is the label inset. */
  insetLabel?: boolean
}

/**
 * State picker component.
 *
 * @returns The state picker component.
 * @notExported
 */
const StatePicker: React.FC<IStatePickerProps> = ({
  id,
  value,
  onChange,
  label = 'State',
  required = false,
  disabled,
  stateOptions,
  insetLabel,
}) => {
  const labelId = React.useId()

  const getSelectedValue = () => {
    if (value) {
      const selectedValueItem = stateOptions.find(item => item.state === value)
      return selectedValueItem
    } else {
      return stateOptions[0]
    }
  }

  return (
    <>
      {!insetLabel && <CaleoInputLabel id={labelId} label={label} required={required} />}
      <Autocomplete
        id={id}
        filterOptions={(options, { inputValue }) => fuseFiltering(options, inputValue, ['label'])}
        disabled={disabled}
        value={getSelectedValue()}
        options={stateOptions}
        fullWidth
        selectOnFocus
        disableClearable
        getOptionLabel={option => option.label}
        isOptionEqualToValue={(selectedValue, option) => option.state === selectedValue.state}
        style={{ marginTop: '0px' }}
        onChange={(_event, newValue) => {
          if (newValue !== null) {
            onChange(newValue.state as unknown as IAllocation)
          }
        }}
        renderInput={params => (
          <InputField
            slotProps={{
              inputLabel: insetLabel ? { shrink: true } : {},
              htmlInput: params?.inputProps,
              input: params?.InputProps,
            }}
            {...params}
            insetLabel={insetLabel}
            label={insetLabel ? label : undefined}
            ariaLabelledBy={!insetLabel ? labelId : undefined}
            name="state"
            size="small"
            margin="dense"
            fullWidth
            variant="outlined"
            required={required}
          />
        )}
      />
    </>
  )
}

export default StatePicker
